/**
 * CombatManager - Manages combat interactions between players and enemies
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';

export class CombatManager {
    constructor(player, inventoryManager, scene, options = {}) {
        this.player = player;
        this.inventoryManager = inventoryManager;
        
        // Store scene reference properly
        // Check if scene is a Three.js Scene directly or contained within an object
        if (scene && typeof scene.add === 'function') {
            this.scene = scene; // Direct Three.js Scene
        } else if (scene && scene.scene && typeof scene.scene.add === 'function') {
            this.scene = scene.scene; // Object containing a scene
        } else {
            console.warn('Invalid scene passed to CombatManager. Projectiles may not render correctly.');
            this.scene = scene; // Store anyway, but warn
        }
        
        // Store camera reference - this is a key change
        // First check if passed in scene is actually the SceneManager
        if (scene && scene.camera) {
            this.camera = scene.camera;
            console.log('Using SceneManager camera for CombatManager');
        } else if (window.game && window.game.camera) {
            // Fallback to game's camera
            this.camera = window.game.camera;
            console.log('Using global game camera for CombatManager');
        } else if (options.camera) {
            // Use camera from options if provided
            this.camera = options.camera;
            console.log('Using provided camera for CombatManager');
        } else {
            console.warn('No camera reference found for CombatManager. Attacks may not work correctly.');
            this.camera = null;
        }
        
        // Target enemy registry
        this.enemies = new Map();
        
        // Collision detection system
        this.raycaster = new THREE.Raycaster();
        
        // Socket connection for multiplayer sync
        this.socket = options.socket || null;
        
        // Control flags
        this.isMobile = options.isMobile || false;
        
        // Last attack time tracking
        this.lastAttackTime = 0;
        
        // Click or tap position
        this.attackPoint = new THREE.Vector2();
        
        // Attack cooldown (in seconds)
        this.cooldown = 0.2;
        
        // Physics system for projectiles
        this.physicsManager = new PhysicsManager();
        
        // Track projectiles - Maps projectileId to projectile object
        this.projectiles = new Map();
        
        // Track active attack touch
        this.activeAttackTouch = null;
        
        // Event bindings
        this.onMouseClick = this.handleMouseClick.bind(this);
        this.onTouchStart = this.handleTouchStart.bind(this);
        this.onTouchEnd = this.handleTouchEnd.bind(this);
        
        // Bind events
        this.bindEvents();
    }
    
    /**
     * Initialize the combat manager
     */
    init() {
        console.log('Initializing Combat Manager');
        
        if (!this.player || !this.inventoryManager) {
            console.error('Cannot initialize combat manager: missing player or inventory manager');
            return false;
        }
        
        // Set up socket event handlers for projectiles
        this.setupSocketHandlers();
        
        return true;
    }
    
    /**
     * Set up socket event handlers for projectiles
     */
    setupSocketHandlers() {
        if (!this.socket) {
            console.warn('No socket provided, projectile sync will not work');
            return;
        }
        
        // Listen for projectile spawning from other players
        this.socket.on('spawn-projectile', (data) => {
            // Only create visuals for projectiles from other players
            if (data.playerId !== this.socket.id) {
                this.createProjectileVisual(data);
            }
        });
        
        // Listen for projectile position updates
        this.socket.on('update-projectile', (data) => {
            this.updateProjectilePosition(data);
        });
        
        // Listen for projectile destruction
        this.socket.on('destroy-projectile', (data) => {
            this.removeProjectileVisual(data.id, data.hit);
        });
        
        // Listen for melee attacks from other players
        this.socket.on('melee-attack', (data) => {
            // We can add visual effects for melee attacks here if needed
            // For now, we'll just log it
            console.log(`Melee attack from player ${data.playerId}`);
        });
    }
    
    /**
     * Bind input events for combat
     */
    bindEvents() {
        // Desktop: mouse down to attack (instead of click)
        document.addEventListener('mousedown', this.onMouseClick);
        
        // Mobile: touch to attack
        if (this.isMobile) {
            document.addEventListener('touchstart', this.onTouchStart, { passive: false });
            document.addEventListener('touchend', this.onTouchEnd);
        }
        
        // Keyboard: 1-2-3-4 to switch inventory slots
        // Note: Currently no effect as weapons are now attached to character models
        // Will be used for future inventory items like buffs and powers
        document.addEventListener('keydown', (event) => {
            // Check for 1, 2, 3, 4 keys to switch inventory slots
            if (event.key >= '1' && event.key <= '4') {
                const slotIndex = parseInt(event.key) - 1;
                this.inventoryManager.setActiveSlot(slotIndex);
            }
        });
    }
    
    /**
     * Handle mouse click for attack
     * @param {MouseEvent} event - Mouse event
     */
    handleMouseClick(event) {
        if (!this.player || !this.inventoryManager) return;
        
        // Remove cooldown check to allow continuous attacks
        // const currentTime = performance.now() / 1000;
        // if (currentTime - this.lastAttackTime < this.cooldown) return;
        
        this.lastAttackTime = performance.now() / 1000;
        
        // Get normalized device coordinates for raycasting
        this.attackPoint.x = (event.clientX / window.innerWidth) * 2 - 1;
        this.attackPoint.y = -(event.clientY / window.innerHeight) * 2 + 1;
        
        // Perform attack
        this.performAttack(this.attackPoint);
    }
    
    /**
     * Handle touch start for attack on mobile
     * @param {TouchEvent} event - Touch event
     */
    handleTouchStart(event) {
        if (!this.player || !this.inventoryManager) return;
        
        // Get the attack button element
        const attackButton = document.querySelector('.attack-button');
        if (!attackButton) return;
        
        // Get the button's position and dimensions
        const buttonRect = attackButton.getBoundingClientRect();
        
        // Check each touch point
        for (const touch of event.changedTouches) {
            // Check if the touch was within the attack button area
            if (touch.clientX >= buttonRect.left && 
                touch.clientX <= buttonRect.right && 
                touch.clientY >= buttonRect.top && 
                touch.clientY <= buttonRect.bottom) {
                
                // Store the active touch ID
                this.activeAttackTouch = touch.identifier;
                
                // For mobile, always shoot in the center direction but slightly higher
                this.attackPoint.x = 0; // Center of screen
                this.attackPoint.y = 0; // Slightly above center (negative y is up in screen coordinates)
                
                // Perform attack
                this.performAttack(this.attackPoint);
                
                // Prevent default to avoid any unwanted behavior
                event.preventDefault();
            }
        }
    }
    
    /**
     * Handle touch end for attack on mobile
     * @param {TouchEvent} event - Touch event
     */
    handleTouchEnd(event) {
        if (!this.player || !this.inventoryManager) return;
        
        // Check if the ended touch was our active attack touch
        for (const touch of event.changedTouches) {
            if (touch.identifier === this.activeAttackTouch) {
                this.activeAttackTouch = null;
            }
        }
    }
    
    /**
     * Perform an attack at the specified screen point
     * @param {THREE.Vector2} point - Screen point in normalized device coordinates
     */
    performAttack(point) {
        // We'll use the player's built-in attack method which handles its default weapon
        if (!this.player || !this.player.attack) {
            console.warn('Cannot attack: no player or attack method available');
            return;
        }
        
        // Use the camera reference we stored in the constructor
        let camera = this.camera;
        
        // If we don't have a camera, try to get it from other places as a fallback
        if (!camera) {
            // Try to update our camera reference first
            if (window.game && window.game.camera) {
                this.camera = window.game.camera;
                camera = this.camera;
                console.log('Updated CombatManager camera reference from game');
            } else if (this.scene && this.scene.parent && this.scene.parent.camera) {
                camera = this.scene.parent.camera;
            } else if (this.player && this.player.camera) {
                camera = this.player.camera;
            }
        }
        
        if (!camera) {
            console.warn('Cannot attack: camera not found. Make sure camera is accessible to CombatManager');
            return;
        }
        
        // Get attack data directly from the player - No cooldown checks
        const attackData = this.player.attack();
        
        // Proceed with the attack even if attackData is null or undefined
        let attackType = 'default';
        let direction;
        
        // Update raycaster with screen point and camera
        this.raycaster.setFromCamera(point, camera);
        
        // Get mouse direction for aiming
        const mouseDirection = this.raycaster.ray.direction.clone();
        
        // Fallback attack data if the player's attack method returns null/undefined
        if (!attackData) {
            console.log('Creating default attack data since player attack returned null');
            // Determine attack type based on player class
            if (this.player.characterClass === 'mage' || 
                (this.player.weaponType && this.player.weaponType.includes('staff'))) {
                attackType = 'ranged';
            } else if (this.player.characterClass === 'rogue' || 
                      (this.player.weaponType && this.player.weaponType.includes('bow'))) {
                attackType = 'ranged';
            } else {
                attackType = 'melee';
            }
        } else {
            attackType = attackData.type;
        }
        
        // Log useful debug information
        console.log('Performing attack:', {
            attackType: attackType,
            playerPosition: this.player.position,
            direction: mouseDirection
        });
        
        // Handle attack based on type
        if (attackType === 'melee') {
            if (attackData) {
                this.performMeleeAttack(attackData);
            } else {
                // Fallback melee attack with default values
                this.performMeleeAttack({
                    damage: 25,
                    range: 2
                });
            }
        } else if (attackType === 'ranged') {
            if (attackData) {
                this.performRangedAttack(attackData, point, camera);
            } else {
                // Fallback ranged attack
                this.performRangedAttack({
                    damage: 25,
                    range: 20
                }, point, camera);
            }
        }
        
        // Sync attack with server if socket is available
        if (this.socket) {
            this.socket.emit('player-attack', {
                attackType: attackType,
                weaponName: this.player.weaponType,
                position: this.player.position,
                direction: {
                    x: mouseDirection.x,
                    y: this.player.rotation.y,  // We use player's y rotation for direction
                    z: mouseDirection.z
                }
            });
        }
    }
    
    /**
     * Perform a melee attack
     * @param {Object} attackData - Attack data from weapon
     */
    performMeleeAttack(attackData) {
        if (!this.player || !this.player.position) return;
        
        // Get targets in melee range
        const targets = this.findTargetsInRadius(
            this.player.position,
            attackData.range || 2  // Default to 2 units range for melee
        );
        
        // Apply damage to targets
        targets.forEach(target => {
            this.applyDamage(target, attackData.damage || 25);  // Default to 25 damage for melee
        });
    }
    
    /**
     * Perform a ranged attack
     * @param {Object} attackData - Attack data from player
     * @param {THREE.Vector2} screenPoint - Screen point for aiming
     * @param {THREE.Camera} camera - Camera for raycasting
     */
    performRangedAttack(attackData, screenPoint, camera) {
        if (!this.player || !this.player.position) {
            console.warn('Cannot perform ranged attack: player position not available');
            return;
        }
        
        // Update raycaster with screen point and camera
        this.raycaster.setFromCamera(screenPoint, camera);
        
        // Get the direction vector from the raycaster
        const direction = this.raycaster.ray.direction.clone().normalize();
        
        // Log direction for debugging
        console.log('Ranged attack direction:', direction);
        
        // Determine projectile type based on weapon
        let projectileType = 'default';
        if (this.player.weaponType && typeof this.player.weaponType === 'string') {
            if (this.player.weaponType.includes('bow') || this.player.weaponType.includes('cross')) {
                projectileType = 'arrow';  // Rogue with crossbow
            } else if (this.player.weaponType.includes('staff')) {
                projectileType = 'orb';    // Mage with staff
            }
        } else {
            // Default fallback based on player class
            if (this.player.characterClass === 'mage') {
                projectileType = 'orb';
            } else if (this.player.characterClass === 'rogue') {
                projectileType = 'arrow';
            }
        }
        
        console.log('Projectile type:', projectileType);
        
        // Create a visual projectile for the local player
        // This will be immediately visible while we wait for server confirmation
        const projectile = this.createLocalProjectile(
            this.player.position.clone(),
            direction,
            projectileType,
            attackData.damage || 25  // Pass the damage value from attackData
        );
        
        // The server will handle the actual projectile physics and broadcast to other players
    }
    
    /**
     * Create a local projectile for immediate visual feedback
     * @param {THREE.Vector3} position - Starting position
     * @param {THREE.Vector3} direction - Direction vector
     * @param {string} type - Projectile type ('arrow' or 'orb')
     * @param {number} damage - Damage value for the projectile
     * @returns {Object} - The created projectile object
     */
    createLocalProjectile(position, direction, type, damage = 25) {
        // Create a temporary ID for this local projectile
        const tempId = 'local_' + Date.now();
        
        // Make sure position and direction are valid
        if (!position || !direction) {
            console.error('Invalid position or direction for projectile');
            return null;
        }
        
        // Ensure position has x, y, z properties
        const startPos = {
            x: position.x || 0,
            y: 2.25, // Slightly lower fixed height above ground for consistent projectile path
            z: position.z || 0
        };
        
        // Ensure direction is normalized
        const dir = {
            x: direction.x || 0,
            y: 0, // Set y direction to 0 to maintain horizontal path
            z: direction.z || 0
        };
        
        // Normalize if possible
        const dirLength = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
        if (dirLength > 0) {
            dir.x /= dirLength;
            dir.z /= dirLength;
        } else {
            // Default to forward if direction is invalid
            dir.z = 1;
        }
        
        // Offset slightly in direction
        startPos.x += dir.x * 0.5;
        startPos.z += dir.z * 0.5;
        
        console.log('Creating projectile:', {
            id: tempId,
            position: startPos,
            direction: dir,
            type: type,
            damage: damage
        });
        
        // Get player ID for sourceId to enable visual effects
        const playerId = this.socket ? this.socket.id : 'player';
        
        // Create the visual projectile
        const visual = this.createProjectileVisual({
            id: tempId,
            position: startPos,
            velocity: {
                x: dir.x * 50,  // 50 units/sec
                y: dir.y * 50,  // Allow vertical component
                z: dir.z * 50   // 50 units/sec
            },
            type: type,
            sourceId: playerId, // Add sourceId to identify as player projectile
            damage: damage      // Add the specified damage value
        });
        
        // Add to local projectiles with limited lifetime (will be replaced by server version)
        setTimeout(() => {
            this.removeProjectileVisual(tempId);
        }, 1000);  // Increased from 200ms to 1000ms for better visibility
        
        return visual;
    }
    
    /**
     * Create a visual representation of a projectile
     * @param {Object} data - Projectile data from server
     * @returns {Object} - The created projectile object with mesh
     */
    createProjectileVisual(data) {
        // If projectile is created without a sourceId, check if it's a player projectile
        if (!data.sourceId && this.socket) {
            // If no sourceId is provided, assume it's from this player
            data.sourceId = this.socket.id;
        }
        
        // Get the projectile color based on source
        let projectileColor = 0xffffff; // Default white
        
        if (data.sourceId) {
            if (data.sourceId.startsWith('enemy_')) {
                // Enemy projectile - red
                projectileColor = 0xff0000;
            } else if (data.type === 'orb') {
                // Player mage projectile - blue
                projectileColor = 0x00ffff;
            } else if (data.type === 'arrow') {
                // Player rogue projectile - orange/gold
                projectileColor = 0xffaa00;
            }
        }
        
        // Create projectile object to pass to ProjectileManager
        const projectileObj = {
            id: data.id,
            position: data.position,
            velocity: data.velocity,
            type: data.type === 'orb' ? 'magic' : data.type, // Convert 'orb' to 'magic' for ProjectileManager
            color: projectileColor,
            damage: data.damage || 25, // Use original damage value instead of 50
            sourceId: data.sourceId || (this.socket ? this.socket.id : 'player'),
            lifetime: data.lifetime || 5 // 5 seconds default lifetime
        };
        
        // If we have a ProjectileManager available, use it for enhanced visuals
        if (window.game && window.game.projectileManager) {
            const projectile = window.game.projectileManager.addProjectile(projectileObj);
            
            if (projectile) {
                // Return the projectile from ProjectileManager
                return {
                    id: data.id,
                    mesh: projectile.mesh,
                    velocity: data.velocity,
                    startPosition: { ...data.position },
                    startTime: Date.now(),
                    type: data.type,
                    managed: true // Flag that this is managed by ProjectileManager
                };
            }
        }
        
        // If ProjectileManager not available, fallback to direct Three.js creation
        let geometry, material, mesh, light;
        
        // Create geometry and material based on projectile type
        if (data.type === 'orb') {
            // Mage orb (blue glowing sphere)
            geometry = new THREE.SphereGeometry(0.2, 8, 8);
            material = new THREE.MeshStandardMaterial({
                color: projectileColor,
                emissive: projectileColor,
                emissiveIntensity: 1
            });
            mesh = new THREE.Mesh(geometry, material);
            
            // Add point light for glow effect
            light = new THREE.PointLight(projectileColor, 1, 5);
            light.position.set(0, 0, 0);
            mesh.add(light);
        } else if (data.type === 'arrow') {
            // Rogue arrow (red glowing cylinder)
            geometry = new THREE.CylinderGeometry(0.02, 0.02, 0.5, 8);
            material = new THREE.MeshStandardMaterial({
                color: projectileColor,
                emissive: projectileColor,
                emissiveIntensity: 1
            });
            mesh = new THREE.Mesh(geometry, material);
            
            // Rotate to point along direction of travel
            mesh.rotation.x = Math.PI / 2;
            
            // Add point light for glow effect
            light = new THREE.PointLight(projectileColor, 1, 5);
            light.position.set(0, 0, 0);
            mesh.add(light);
        } else {
            // Default projectile (white sphere)
            geometry = new THREE.SphereGeometry(0.1, 8, 8);
            material = new THREE.MeshBasicMaterial({ color: projectileColor });
            mesh = new THREE.Mesh(geometry, material);
        }
        
        // Set initial position
        mesh.position.set(
            data.position.x,
            data.position.y,
            data.position.z
        );
        
        // Add to scene - Fix the scene reference
        if (this.scene && typeof this.scene.add === 'function') {
            this.scene.add(mesh);
        } else if (this.scene && this.scene.scene && typeof this.scene.scene.add === 'function') {
            // If scene is actually an object containing the scene
            this.scene.scene.add(mesh);
        } else {
            console.error('Cannot add projectile to scene: scene.add is not a function');
        }
        
        // Create projectile object with mesh and data
        const projectile = {
            id: data.id,
            mesh: mesh,
            velocity: data.velocity,
            startPosition: { ...data.position },
            startTime: Date.now(),
            type: data.type,
            sourceId: data.sourceId
        };
        
        // Store in projectiles map
        this.projectiles.set(data.id, projectile);
        
        return projectile;
    }
    
    /**
     * Update a projectile's position based on server data
     * @param {Object} data - Projectile update data from server
     */
    updateProjectilePosition(data) {
        const projectile = this.projectiles.get(data.id);
        if (!projectile || !projectile.mesh) return;
        
        // Update the mesh position
        projectile.mesh.position.set(
            data.position.x,
            data.position.y,
            data.position.z
        );
    }
    
    /**
     * Remove a projectile's visual representation
     * @param {string} id - Projectile ID
     * @param {boolean} hit - Whether the projectile hit something (for visual effects)
     */
    removeProjectileVisual(id, hit = false) {
        const projectile = this.projectiles.get(id);
        if (!projectile || !projectile.mesh) return;
        
        // If hit is true, we can add an impact visual effect here
        if (hit) {
            // TODO: Add impact effects in a future step
        }
        
        // Remove from scene - Fix the scene reference
        if (this.scene && typeof this.scene.remove === 'function') {
            this.scene.remove(projectile.mesh);
        } else if (this.scene && this.scene.scene && typeof this.scene.scene.remove === 'function') {
            // If scene is actually an object containing the scene
            this.scene.scene.remove(projectile.mesh);
        } else {
            console.error('Cannot remove projectile from scene: scene.remove is not a function');
        }
        
        // Remove from projectiles map
        this.projectiles.delete(id);
        
        // Dispose of geometries and materials to prevent memory leaks
        if (projectile.mesh.geometry) projectile.mesh.geometry.dispose();
        if (projectile.mesh.material) {
            if (Array.isArray(projectile.mesh.material)) {
                projectile.mesh.material.forEach(material => material.dispose());
            } else {
                projectile.mesh.material.dispose();
            }
        }
    }
    
    /**
     * Find targets in a radius around a position
     * @param {THREE.Vector3} position - Center position
     * @param {number} radius - Radius to search
     * @returns {Array} - Array of targets (enemies/players)
     */
    findTargetsInRadius(position, radius) {
        // Validate position and radius
        if (!position || !radius) {
            console.error('Invalid position or radius for findTargetsInRadius');
            return [];
        }

        console.log(`Finding targets within ${radius} units of position:`, position);
        
        // List to store valid targets
        const targets = [];
        
        // Check enemies
        if (window.game && window.game.enemyManager) {
            console.log('Checking game.enemyManager for enemies');
            // Get all enemies from the game instance if available
            let enemies = [];
            if (typeof window.game.enemyManager.getEnemies === 'function') {
                // If there's a getEnemies method, use it
                enemies = Object.values(window.game.enemyManager.getEnemies());
            } else if (window.game.enemyManager.enemies instanceof Map) {
                // If it's a Map, convert to array
                enemies = Array.from(window.game.enemyManager.enemies.values());
            } else if (window.game.enemyManager.enemies) {
                // If it's an object, get values
                enemies = Object.values(window.game.enemyManager.enemies);
            }
            
            console.log(`Found ${enemies.length} enemies to check for melee attack targets`);
            
            // Check each enemy's distance
            enemies.forEach(enemy => {
                if (!enemy || !enemy.position) return;
                
                // Calculate distance
                const dx = position.x - enemy.position.x;
                const dy = position.y - enemy.position.y;
                const dz = position.z - enemy.position.z;
                const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
                
                // Check if within range
                if (distance <= radius) {
                    console.log(`Enemy ${enemy.id} is within attack range (${distance.toFixed(2)} units)`);
                    targets.push(enemy);
                }
            });
        } else {
            console.warn('No game.enemyManager found, using local enemies');
            // Use our own enemies registry if no game instance or fallback
            this.enemies.forEach(enemy => {
                if (!enemy || !enemy.position) return;
                
                // Calculate distance
                const dx = position.x - enemy.position.x;
                const dy = position.y - enemy.position.y;
                const dz = position.z - enemy.position.z;
                const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);
                
                // Check if within range
                if (distance <= radius) {
                    targets.push(enemy);
                }
            });
        }
        
        console.log(`Found ${targets.length} targets within range`);
        return targets;
    }
    
    /**
     * Apply damage to a target
     * @param {Object} target - Target object (player or enemy)
     * @param {number} amount - Amount of damage to apply
     */
    applyDamage(target, amount) {
        if (!target) return;
        
        // Check if the target is an enemy
        if (target.id && target.id.includes('enemy_')) {
            console.log(`Applying ${amount} damage to enemy ${target.id}`);
            
            // Apply a visual indication of damage
            if (target.mesh) {
                // Flash the enemy red
                const originalMaterial = target.mesh.material.clone();
                target.mesh.material.color.set(0xff0000);
                
                // Reset after 150ms
                setTimeout(() => {
                    target.mesh.material.color.copy(originalMaterial.color);
                }, 150);
            }
            
            // Send damage event to the server
            if (this.socket) {
                console.log(`Emitting enemyDamage event for enemy ${target.id} with damage ${amount}`);
                this.socket.emit('enemyDamage', {
                    enemyId: target.id,
                    damage: amount
                });
            } else if (window.game && window.game.socket) {
                // Fallback to game.socket if this.socket is not available
                console.log(`Using game.socket to emit enemyDamage event for enemy ${target.id}`);
                window.game.socket.emit('enemyDamage', {
                    enemyId: target.id,
                    damage: amount
                });
            } else {
                console.error('No socket available to send damage event!');
            }
            
            // Note: The actual damage is applied server-side
        } else {
            // For players, apply damage locally (this might be moved server-side in the future)
            if (typeof target.takeDamage === 'function') {
                target.takeDamage(amount);
            }
        }
    }
    
    /**
     * Register an enemy for combat targeting
     * @param {string} id - Unique ID for the enemy
     * @param {Object} enemy - Enemy object with position and takeDamage method
     */
    registerEnemy(id, enemy) {
        this.enemies.set(id, enemy);
    }
    
    /**
     * Unregister an enemy
     * @param {string} id - Unique ID for the enemy
     */
    unregisterEnemy(id) {
        this.enemies.delete(id);
    }
    
    /**
     * Update combat state
     * @param {number} deltaTime - Time since last update in seconds
     */
    update(deltaTime) {
        // Update physics
        this.physicsManager.update(deltaTime);
        
        // Update local projectiles for client-side prediction
        this.updateLocalProjectiles(deltaTime);
    }
    
    /**
     * Update local projectiles for client-side prediction
     * @param {number} deltaTime - Time since last update in seconds
     */
    updateLocalProjectiles(deltaTime) {
        // Only update projectiles that start with 'local_'
        this.projectiles.forEach((projectile, id) => {
            if (id.startsWith('local_') && projectile.mesh) {
                // Store old position for hit detection
                const oldPosition = projectile.mesh.position.clone();
                
                // Calculate new position based on velocity and time
                projectile.mesh.position.x += projectile.velocity.x * deltaTime;
                projectile.mesh.position.y += projectile.velocity.y * deltaTime;
                projectile.mesh.position.z += projectile.velocity.z * deltaTime;
                
                // Check for collisions with enemies
                this.checkProjectileCollision(projectile, id, oldPosition);
            }
        });
    }
    
    /**
     * Check for projectile collisions with enemies
     * @param {Object} projectile - The projectile object
     * @param {string} projectileId - The projectile ID
     * @param {THREE.Vector3} oldPosition - The previous position of the projectile
     */
    checkProjectileCollision(projectile, projectileId, oldPosition) {
        if (!projectile || !projectile.mesh) return;
        
        // We'll use raycasting from the old position to the new position
        const direction = new THREE.Vector3()
            .subVectors(projectile.mesh.position, oldPosition)
            .normalize();
            
        // Calculate the distance moved this frame
        const distance = projectile.mesh.position.distanceTo(oldPosition);
        
        // Set up the raycaster
        const raycaster = new THREE.Raycaster(oldPosition, direction, 0, distance);
        
        // Get potential enemy targets
        const targets = [];
        
        // Check the global game object for enemies
        if (window.game && window.game.enemyManager) {
            // Get all enemies from the game instance if available
            let enemies = [];
            
            if (window.game.enemyManager.enemies instanceof Map) {
                // If it's a Map, convert to array of values
                enemies = Array.from(window.game.enemyManager.enemies.values());
            } else if (typeof window.game.enemyManager.getEnemies === 'function') {
                // If there's a getEnemies method, use it
                enemies = Object.values(window.game.enemyManager.getEnemies());
            } else if (window.game.enemyManager.enemies) {
                // If it's an object, get values
                enemies = Object.values(window.game.enemyManager.enemies);
            }
            
            // Get meshes for all enemies
            enemies.forEach(enemy => {
                if (enemy && enemy.mesh) {
                    targets.push(enemy.mesh);
                }
            });
        }
        
        // Also check our own enemies registry (use either, both is fine for redundancy)
        this.enemies.forEach(enemy => {
            if (enemy && enemy.mesh) {
                targets.push(enemy.mesh);
            }
        });
        
        // Perform intersection test
        const intersects = raycaster.intersectObjects(targets, false);
        
        if (intersects.length > 0) {
            // We hit something!
            const hit = intersects[0];
            const hitObject = hit.object;
            
            // Make sure it's an enemy
            if (hitObject.userData && hitObject.userData.type === 'enemy') {
                console.log(`Projectile ${projectileId} hit enemy ${hitObject.userData.id}`);
                
                // Apply damage based on projectile type
                const damage = projectile.type === 'orb' ? 20 : 30; // Arrows do more damage
                this.applyDamage({id: hitObject.userData.id, mesh: hitObject}, damage);
                
                // Remove the projectile
                this.removeProjectileVisual(projectileId, true);
                
                // Add a hit effect at the point of impact
                this.createHitEffect(hit.point, projectile.type);
            }
        }
    }
    
    /**
     * Create a visual effect at the hit point
     * @param {THREE.Vector3} position - The position of the hit
     * @param {string} projectileType - The type of projectile that hit
     */
    createHitEffect(position, projectileType) {
        // Skip if no scene
        if (!this.scene) return;
        
        // Get color based on projectile type
        let color = 0xffffff;
        if (projectileType === 'orb') {
            color = 0x0088ff;
        } else if (projectileType === 'arrow') {
            color = 0xff4400;
        }
        
        // Create a small particle burst at the hit location
        const particleCount = 10;
        const particles = new THREE.Group();
        
        for (let i = 0; i < particleCount; i++) {
            // Create a small sphere for each particle
            const geometry = new THREE.SphereGeometry(0.05, 4, 4);
            const material = new THREE.MeshBasicMaterial({
                color: color,
                transparent: true,
                opacity: 0.8
            });
            const particle = new THREE.Mesh(geometry, material);
            
            // Position at hit point
            particle.position.copy(position);
            
            // Set random velocity
            particle.velocity = new THREE.Vector3(
                (Math.random() - 0.5) * 3,
                (Math.random() - 0.5) * 3,
                (Math.random() - 0.5) * 3
            );
            
            particles.add(particle);
        }
        
        // Add to scene
        this.scene.add(particles);
        
        // Animate particles
        let lifetime = 0;
        const maxLifetime = 0.5; // 0.5 seconds
        
        const animate = (deltaTime) => {
            lifetime += deltaTime;
            
            if (lifetime >= maxLifetime) {
                // Remove particles from scene
                this.scene.remove(particles);
                
                // Dispose of geometries and materials
                particles.children.forEach(particle => {
                    particle.geometry.dispose();
                    particle.material.dispose();
                });
                
                return;
            }
            
            // Update particle positions
            particles.children.forEach(particle => {
                particle.position.x += particle.velocity.x * deltaTime;
                particle.position.y += particle.velocity.y * deltaTime;
                particle.position.z += particle.velocity.z * deltaTime;
                
                // Fade out
                particle.material.opacity = 0.8 * (1 - lifetime / maxLifetime);
                
                // Slow down
                particle.velocity.multiplyScalar(0.95);
            });
            
            // Request next frame
            requestAnimationFrame(() => animate(1/60));
        };
        
        // Start animation
        animate(1/60);
    }
    
    /**
     * Clean up resources
     */
    dispose() {
        // Remove event listeners
        document.removeEventListener('mousedown', this.onMouseClick);
        if (this.isMobile) {
            document.removeEventListener('touchstart', this.onTouchStart, { passive: false });
            document.removeEventListener('touchend', this.onTouchEnd);
        }
        
        // Clean up projectiles
        this.projectiles.forEach((projectile, id) => {
            this.removeProjectileVisual(id);
        });
        
        // Clear projectiles map
        this.projectiles.clear();
    }
}

/**
 * PhysicsManager - Client-side physics management for projectiles
 */
class PhysicsManager {
    constructor() {
        // Create a Cannon.js world
        this.world = new CANNON.World();
        
        // Disable gravity for bullet-like projectiles
        this.world.gravity.set(0, 0, 0);
    }
    
    /**
     * Update the physics world
     * @param {number} deltaTime - Time since last update in seconds
     */
    update(deltaTime) {
        // Step the physics simulation
        this.world.step(deltaTime);
    }
}

export { PhysicsManager }; 